import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '@/shared/components/atoms/ui/badge'

/**
 * Shared visual primitives for the "info-request" UX surfaces.
 *
 * Submitter side (request is in `PENDING_INFO`):
 *   - `<ActionNeededRowDecor>` wraps a list row in an amber left-border +
 *     faint amber background so the row reads as urgent at a glance.
 *   - `<ReplyRequiredChip>` is the small inline badge ("Reply required")
 *     used on the request-detail header and the row title bar.
 *
 * Approver side (a side-loop info-gate has been answered):
 *   - `<SubmitterRepliedPill>` is the teal pill rendered next to the
 *     urgency pill on `<ApprovalRow>`.
 *
 * All three colors are coordinated with `_stateBadge.tsx`:
 *   - Amber (submitter action needed) matches `PENDING_INFO`.
 *   - Teal  (approver positive signal) matches `APPROVED`.
 */

interface ActionNeededRowDecorProps {
	to: string
	children: ReactNode
	dataTestId?: string
}

/**
 * Decorated link wrapper for a "needs your reply" row. Renders an `<a>`
 * around the children with amber left-border + faint amber background + the
 * "Reply now →" CTA inline-right.
 *
 * The caller still composes the inner card; this is a layout wrapper, not a
 * card component, so the existing `<Card>` content from the list keeps
 * working unchanged.
 */
export function ActionNeededRowDecor({ to, children, dataTestId }: ActionNeededRowDecorProps) {
	return (
		<Link
			to={to}
			className="block focus:outline-none"
			data-testid={dataTestId}
			aria-label="Information request — reply now"
		>
			<div className="relative overflow-hidden rounded-lg border border-amber-300 bg-amber-50/60 transition-colors hover:border-amber-400 focus-visible:border-amber-500">
				<div className="absolute inset-y-0 left-0 w-1 bg-amber-400" aria-hidden />
				<div className="flex items-stretch gap-3 pl-3">
					<div className="min-w-0 flex-1">{children}</div>
					<div className="flex items-center pr-4 text-sm font-medium text-amber-900">Reply now →</div>
				</div>
			</div>
		</Link>
	)
}

/**
 * Small badge used inline near a request title to say "this request needs
 * your reply". Co-located with `ActionNeededRowDecor` so the visual language
 * stays in one file.
 */
export function ReplyRequiredChip({ dataTestId }: { dataTestId?: string } = {}) {
	return (
		<Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800" data-testid={dataTestId}>
			Reply required
		</Badge>
	)
}

/**
 * Teal "Submitter replied" pill for the approver-side approval row.
 * Rendered when the approval has an `answeredInfoGate` that hasn't been
 * acted on yet (the FE decides — backend just surfaces the most-recent
 * completed gate per parent).
 */
export function SubmitterRepliedPill({ completedAt, dataTestId }: { completedAt?: string; dataTestId?: string }) {
	const relative = completedAt ? ` · ${formatRelative(completedAt)}` : ''
	return (
		<span
			className="rounded bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700"
			data-testid={dataTestId}
			aria-label="Submitter replied to the information request"
		>
			Submitter replied{relative}
		</span>
	)
}

function formatRelative(iso: string): string {
	const diffMs = Date.now() - new Date(iso).getTime()
	const minutes = Math.round(diffMs / 60000)
	if (minutes < 1) return 'just now'
	if (minutes < 60) return `${minutes}m ago`
	const hours = Math.round(minutes / 60)
	if (hours < 48) return `${hours}h ago`
	const days = Math.round(hours / 24)
	return `${days}d ago`
}
